import { Check, ExternalLink, Minus } from "lucide-react"

import { cn } from "@/lib/utils"
import { BRIEF_COPY } from "./copy"

// A yes or no answer. "No" recedes: it is an answer, not something to act on.
export function YesNo({ value }) {
  const Icon = value ? Check : Minus

  return (
    <span className={cn("inline-flex items-center gap-1.5", !value && "font-medium text-ink-3")}>
      <Icon className="size-[15px] shrink-0" strokeWidth={2.4} />
      {value ? BRIEF_COPY.yes : BRIEF_COPY.no}
    </span>
  )
}

export function Chips({ items, className }) {
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {items.map((item) => (
        <span
          key={item}
          className="rounded-[7px] border border-border bg-secondary px-[10px] py-1 text-[12.5px] leading-[1.35] font-semibold text-foreground [overflow-wrap:anywhere]"
        >
          {item}
        </span>
      ))}
    </div>
  )
}

function FactValue({ fact }) {
  if (fact.kind === "bool") return <YesNo value={fact.value} />
  if (fact.kind === "list") return <Chips items={fact.value} />

  if (fact.kind === "url") {
    return (
      <a
        href={fact.value}
        target="_blank"
        rel="noopener noreferrer"
        title={fact.value}
        className="flex w-fit max-w-full items-center gap-1.5 text-foreground hover:text-primary"
      >
        <span className="truncate underline decoration-line-2 underline-offset-[3px]">
          {fact.value.replace(/^https?:\/\/(www\.)?/, "")}
        </span>
        <ExternalLink className="size-[13px] shrink-0 text-ink-3" strokeWidth={2} />
      </a>
    )
  }

  return (
    <>
      {fact.value}
      {fact.sub && <span className="mt-px block text-[12.5px] font-medium text-muted-foreground">{fact.sub}</span>}
    </>
  )
}

// One answer: its label over its value. Lists, links and long values take the
// grid's full row.
export function Fact({ fact, children }) {
  const wide = fact.wide || fact.kind === "list" || fact.kind === "url"

  return (
    <div className={cn("min-w-0", wide && "col-span-full")}>
      <dt className="mb-1 text-[12px] leading-[1.35] font-semibold text-pretty text-ink-3">{fact.label}</dt>
      <dd className="text-[14px] leading-[1.4] font-semibold text-foreground [overflow-wrap:anywhere]">
        <FactValue fact={fact} />
        {children}
      </dd>
    </div>
  )
}

// The order's short answers as a data sheet (design FactGrid): groups of facts
// split by a hairline, no group titles. The server sends only the facts the
// order has, and only the groups that have any.
export default function FactGrid({ groups = [] }) {
  return groups.map((group, index) => (
    <dl
      key={group.map((fact) => fact.label).join("|")}
      className={cn(
        "grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-x-[22px] gap-y-4 max-[560px]:grid-cols-2 max-[560px]:gap-x-4 max-[560px]:gap-y-[14px]",
        index > 0 && "mt-4 border-t border-border pt-4"
      )}
    >
      {group.map((fact) => (
        <Fact key={fact.label} fact={fact} />
      ))}
    </dl>
  ))
}
